import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import {
  NavigationError,
  provideRouter,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { routes } from '@app/app.routes';
import { authInterceptor } from '@app/interceptors/auth';
import { encryptionDecryptionInterceptor } from '@app/interceptors/encryption-decryption';
import { provideOvinoLottie } from '@app/providers/lottie.providers';

const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;
const CHUNK_RELOAD_KEY = 'chunk-reload-at';

/**
 * After a deploy, tabs still running the old build request lazy chunks that no longer exist.
 * Do a full load of the target URL to pick up the new build (at most once per 10s to avoid loops).
 */
function reloadOnStaleChunk(event: NavigationError): void {
  if (typeof window === 'undefined' || !CHUNK_LOAD_ERROR.test(String(event.error?.message))) {
    return;
  }
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < 10_000) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still reload once
  }
  window.location.assign(event.url);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, encryptionDecryptionInterceptor]),
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withNavigationErrorHandler(reloadOnStaleChunk),
    ),
    provideClientHydration(withEventReplay()),
    provideOvinoLottie(),
  ],
};
